import { Button, theme } from '@healingpaper/cell'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useImpressionTracker from 'react-impression-tracker'
import styled from 'styled-components'
import { createFetchWebEventCollector, EventName, PageName, Predecessor } from 'web-event-collector'
import { IcArrowRight, IcCheckMarkFilled } from '@healingpaper/cell-icons'
import Link from 'next/link'
import { getEnv } from '@unni/platform'

import { AssessmentState } from '@/features/hospital/types/hospital.type'
import { NONMEMBER_ID } from '@/common/constants'
import CheetahIcon, { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon'
import Text from '@/components/atomic/atoms/Text'
import BottomBarInfoButtons from '@/components/atomic/molecules/BottomBarInfoButtons'
import ConfirmPopup from '@/shared/components/confirm-popup'
import { OptionalAgreeType } from '@/components/atomic/organisms/NormalConsultationBottomSheet'
import ReviewLoginCard from '@/components/atomic/organisms/ReviewLoginCard'
import Icon from '@/components/cell/atoms/Icon'
import { IconPack } from '@/components/cell/atoms/Icon/enum'
import { postAmplitude } from '@/core/lib'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { createSignInUrl } from '@/shared/utils/url-utils'
import { createCommonEvent } from '@/utils/eventCollector'
import { generateTagId } from '@/utils/test-automation'
import { PathnamePrefix } from '@/utils/language'
import { useLocales } from '@/shared/hooks/useLocales'
import { HospitalModuleProps, SquareReviewElementProps } from '@/features/doctors/[doctorId]/index/helpers/types'
import { CommunityOfDoctor, DoctorInfo } from '@/features/doctors/[doctorId]/helpers/types/doctor'
import { ActivitiesInCommunitySection } from '@/features/doctors/[doctorId]/index/components/activities-in-community-section'
import { ReplyTooltip } from '@/features/doctors/[doctorId]/index/components/reply-tooltip'
import { DoctorDetailBreadcrumb } from '@/features/doctors/[doctorId]/index/components/doctor-detail-breadcrumb/doctor-detail-breadcrumb'

export interface DoctorDetailTemplateProps {
  userId: number
  userCountry?: string
  pathPrefix: PathnamePrefix
  doctorInfo: DoctorInfo
  reviews: SquareReviewElementProps[]
  hospital: HospitalModuleProps
  community?: CommunityOfDoctor
  optionalMarketingItems: (keyof OptionalAgreeType)[]
  onProfileImageLoaded: () => void
  onClickConsultation: () => void
}

export const DoctorDetailTemplate = (props: DoctorDetailTemplateProps): JSX.Element => {
  const { t } = useTranslation()
  const { locale } = useLocales()
  const router = useRouter()

  const [isImpressionVisible, setIsImpressionVisible] = useState(false)

  const { emitHospitalViewed, emitHospitalClicked } = createFetchWebEventCollector({
    baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
  })

  const BASE_INDEX = 1
  const PROFILE_INDEX = 1

  const DOCTOR_MODULE_INDEX = BASE_INDEX + PROFILE_INDEX + Number(!!props.reviews.length) + Number(!!props.community) // 의사 상세 내 병원 순서 번경 시 수정

  const { targetRef, startTracking } = useImpressionTracker<HTMLAnchorElement, unknown>({
    context: {},
    onImpressed: () => {
      if (props.userId) {
        const commonEvent = createCommonEvent(navigator.userAgent, props.userId)

        emitHospitalViewed({
          ...commonEvent,
          Name: EventName.HospitalViewed,
          Body: {
            ...commonEvent.Body,
            hospitalId: props.hospital.id,
            itemOrderInPage: DOCTOR_MODULE_INDEX, // 의사 상세 내 병원 순서 번경 시 수정
            pageName: PageName.DoctorDetailWeb,
          },
        })
      }
    },
    isImpressionVisible,
    hasImpressed: false,
    rootMargin: '-66px',
  })

  const moveToSignIn = () => {
    router.push(createSignInUrl(`/doctors/${props.doctorInfo.id}`, locale))
  }

  useEffect(() => {
    localStorage && setIsImpressionVisible(localStorage.getItem('isImpressionVisible') === 'true')

    startTracking()
  }, [])

  const [commonAmplitudeProps, setCommonAmplitudeProps] = useState({
    doctor_id: props.doctorInfo.id,
    doctor_name: props.doctorInfo.name,
    hospital_id: props.doctorInfo.hospitalId,
    hospital_name: props.doctorInfo.hospitalName,
    referrer: '',
  })

  const [isLoginConfirmPopupVisible, setIsLoginConfirmPopupVisible] = useState(false)
  const closeLoginConfirmPopup = () => setIsLoginConfirmPopupVisible(false)

  const storePredecessorAndEmitHospitalClicked = () => {
    const commonEvent = createCommonEvent(navigator.userAgent, props.userId)

    const predecessor: Predecessor = {
      Name: EventName.HospitalClicked,
      TraceId: commonEvent.TraceId,
    }

    localStorage.setItem('predecessor', JSON.stringify(predecessor))

    emitHospitalClicked({
      ...commonEvent,
      Name: EventName.HospitalClicked,
      Body: {
        ...commonEvent.Body,
        hospitalId: props.hospital.id,
        itemOrderInPage: DOCTOR_MODULE_INDEX, // 의사 상세 내 병원 순서 번경 시 수정
        pageName: PageName.DoctorDetailWeb,
      },
    })
  }

  const handleHospitalModuleClick = () => {
    postAmplitude('click_hospital_in_doctor_detail', commonAmplitudeProps)

    if (props.userId === NONMEMBER_ID) {
      return
    }

    storePredecessorAndEmitHospitalClicked()
  }

  const isConsultableAndUserIsKoreanMember =
    props.doctorInfo.activeConsultation &&
    (props.userId === NONMEMBER_ID ||
      (props.userId !== NONMEMBER_ID && !!props.userCountry && !!props.userCountry.match(/kr/i)))

  useEffect(() => {
    postAmplitude('view_doctor_detail', { ...commonAmplitudeProps, referrer: document.referrer })

    setCommonAmplitudeProps({
      doctor_id: props.doctorInfo.id,
      doctor_name: props.doctorInfo.name,
      hospital_id: props.doctorInfo.hospitalId,
      hospital_name: props.doctorInfo.hospitalName,
      referrer: document.referrer,
    })
  }, [])

  return (
    <div>
      <DoctorDetailBreadcrumb hospitalName={props.doctorInfo.hospitalName} hospitalId={props.doctorInfo.hospitalId} />
      <StyledProfile>
        <StyledProfileImageWrapper>
          <StyledProfileImage
            data-testid="doctor-profile-image"
            $isSpecialist={props.doctorInfo.specialist}
            src={props.doctorInfo.profileImageUrl}
            layout="fill"
            alt="doctor profile image"
            onLoad={props.onProfileImageLoaded}
          />
          {props.doctorInfo.specialist && (
            <StyledSpecialistCertificatedIcon data-testid="specialist-certificated-icon">
              <Icon name={IconPack.GENERAL_CERTIFICATION_DOCTOR_LATTE} width={24} height={24} />
            </StyledSpecialistCertificatedIcon>
          )}
        </StyledProfileImageWrapper>
        <StyledDoctorProfileDetailWrapper>
          <StyledDoctorNameSpecialtyWrapper>
            <StyledDoctorNameWrapper>
              {props.doctorInfo.name} {props.doctorInfo.chief && t('pages.doctorDetail.chief')}
            </StyledDoctorNameWrapper>
            {props.doctorInfo.specialist && (
              <StyledSpecialistTextWrapper>
                {' '}
                · <StyledSpecialistText>{t('pages.doctorDetail.specialist')}</StyledSpecialistText>
              </StyledSpecialistTextWrapper>
            )}
          </StyledDoctorNameSpecialtyWrapper>
          <StyledDoctorSubtitle data-testid="doctor-subtitle">{props.doctorInfo.hospitalName}</StyledDoctorSubtitle>
          <StyledDoctorSubtitle>
            {/* 후기 */}
            {t('pages.doctorDetail.review')} <b>{props.doctorInfo.reviewCount.toLocaleString()}</b>
            {/* 답변 */}
            {!!props.doctorInfo.communityCommentPostCount && (
              <>
                {' · '}
                {t('pages.doctorDetail.reply')} <b>{props.doctorInfo.communityCommentPostCount.toLocaleString()}</b>
                <ReplyTooltip />
              </>
            )}
          </StyledDoctorSubtitle>
          <StyledTagsSection aria-label="tags-section">
            {props.doctorInfo.treatmentTagNames.map(
              (tag, index) => `${tag}${index !== props.doctorInfo.treatmentTagNames.length - 1 ? ', ' : ''}`,
            )}
          </StyledTagsSection>
        </StyledDoctorProfileDetailWrapper>
      </StyledProfile>
      <StyledDivider />

      {props.community !== undefined &&
        (props.community.documents.content.length > 0 || props.community.comments.content.length > 0) && (
          <ActivitiesInCommunitySection
            community={props.community}
            doctorInfo={props.doctorInfo}
            userId={props.userId}
          />
        )}

      {!!props.reviews.length && (
        <>
          <StyledReviewSectionWrapper>
            <StyledSectionTitle data-testid="review-title">
              {t('pages.doctorDetail.treatmentReview')}{' '}
              <StyledReviewCount>
                {props.doctorInfo.reviewCount >= 10000 ? '9,999+' : props.doctorInfo.reviewCount.toLocaleString()}
              </StyledReviewCount>
              {t('pages.doctorDetail.reviewCountUnit')}
            </StyledSectionTitle>
            {props.userId === NONMEMBER_ID && (
              <div>
                <ReviewLoginCard
                  text={{
                    title: t('review.loginCard.title'),
                    doLogin: t('review.loginCard.doLogin'),
                  }}
                  onClickLoginButton={() => {
                    postAmplitude('click_login_review_module_in_doctor_detail', commonAmplitudeProps)
                    sendAirbridgeEvent(AirbridgeEvents.SIGN_UP_BUTTON_CLICK_IN_WEB, {})
                    moveToSignIn()
                  }}
                />
                <StyledWarning type="description2">{t('review.warning')}</StyledWarning>
              </div>
            )}
            {props.userId !== NONMEMBER_ID && (
              <div>
                <StyledReviewImagesWrapper>
                  {props.reviews.map((review) => (
                    <StyledReviewWrapper key={review.id} data-testid="review-element">
                      <Link
                        aria-label="review-link"
                        href={`/reviews?doctorId=${props.doctorInfo.id}&reviewId=${review.id}`}
                        target="_blank"
                        onClick={() => postAmplitude('click_review_card_in_doctor_detail', commonAmplitudeProps)}
                        rel="noreferrer"
                      >
                        <StyledReviewImage
                          src={review.photo || '/images/reviews/review_placeholder.png'}
                          alt="review photo"
                        />
                      </Link>

                      <StyledTreatmentLabel aria-label="treatment-label">
                        {review.treatments.map((treatment) => `#${treatment?.label ?? treatment?.name}`).join(', ')}
                      </StyledTreatmentLabel>
                    </StyledReviewWrapper>
                  ))}
                </StyledReviewImagesWrapper>
                <StyledReviewButtonLink
                  data-testid="review-button-link"
                  href={`/reviews?doctorId=${props.doctorInfo.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <StyledButton
                    hierarchy="Tertiary"
                    size="Medium"
                    data-testid="review-button"
                    onClick={() => postAmplitude('click_more_review_in_doctor_detail', commonAmplitudeProps)}
                  >
                    <StyledReviewButtonInnerText>
                      {t('pages.doctorDetail.showAllReviewsOfThisDoctor')}
                      <IcArrowRight width={16} height={16} />
                    </StyledReviewButtonInnerText>
                  </StyledButton>
                </StyledReviewButtonLink>
              </div>
            )}
          </StyledReviewSectionWrapper>
          <StyledDivider />
        </>
      )}

      <StyledAffiliatedHospitalSection>
        <StyledSectionTitle>{t('pages.doctorDetail.affiliatedHospital')}</StyledSectionTitle>
        <StyledHospitalModule
          ref={targetRef}
          aria-label="hospital info module"
          href={`/hospitals/${props.hospital.id}`}
          onClick={handleHospitalModuleClick}
        >
          <StyledHospitalProfileImageWrapper>
            <img src={props.hospital.profileImage} width={32} height={32} alt="hospital profile" />
          </StyledHospitalProfileImageWrapper>
          <StyledHospitalInfoWrapper>
            {props.hospital.assessmentState === AssessmentState.CERTIFIED && (
              <StyledCertifiedBadge>
                <IcCheckMarkFilled width={14} height={14} color={theme.colors.certified} />
                <StyledCertifiedHospitalText>{t('hospital.certifiedHospital')}</StyledCertifiedHospitalText>
              </StyledCertifiedBadge>
            )}
            <StyledHospitalName data-testid="hospital-name">{props.hospital.name}</StyledHospitalName>
            <StyledHospitalDetailInfoWrapper>
              {!!props.hospital.subwayName && (
                <StyledHospitalSubwayName data-testid="hospital-subway">
                  {props.hospital.subwayName}
                </StyledHospitalSubwayName>
              )}
              <StyledHospitalRatingArea>
                <StyledStarIcon name={CheetahIconPack.STAR} />
                <span data-testid="hospital-rating">
                  {props.hospital.rating.toFixed(1)}
                  <StyledHospitalRatingCount data-testid="hospital-rating-count">
                    {' '}
                    ({props.hospital.ratingCount.toLocaleString()})
                  </StyledHospitalRatingCount>
                </span>
              </StyledHospitalRatingArea>
              {props.hospital.eventCount > 0 && (
                <StyledEventInfoArea>
                  {`${t('pages.doctorDetail.event')} `}
                  <StyledHospitalEventCount data-testid="hospital-event-count">
                    {props.hospital.eventCount.toLocaleString()}
                  </StyledHospitalEventCount>
                  {t('pages.doctorDetail.eventCountUnit')}
                </StyledEventInfoArea>
              )}
            </StyledHospitalDetailInfoWrapper>
          </StyledHospitalInfoWrapper>
        </StyledHospitalModule>
      </StyledAffiliatedHospitalSection>
      <StyledDivider />

      <StyledExperienceHistoryWrapper>
        <StyledSectionTitle>{t('pages.doctorDetail.experienceHistory')}</StyledSectionTitle>
        <StyledExperienceHistory>{props.doctorInfo.experienceHistory}</StyledExperienceHistory>
      </StyledExperienceHistoryWrapper>

      {isConsultableAndUserIsKoreanMember && (
        <BottomBarInfoButtons
          buttonProps={[
            {
              label: t('pages.doctorDetail.reserveForDoctor'),
              ariaLabelOfLabel: 'consult button',
              id: generateTagId('doctor', 'page', 'btn', 'request'),
              onClick: () => {
                postAmplitude('click_consultation', commonAmplitudeProps)

                // legacy. 추후 click_consultation로 통합합니다. _modn
                postAmplitude('click_consultation_in_doctor_detail', commonAmplitudeProps)

                if (props.userId === NONMEMBER_ID) {
                  setIsLoginConfirmPopupVisible(true)
                  return
                }

                props.onClickConsultation()
                router.push(`/doctors/${props.doctorInfo.id}/inquiry`)
              },
            },
          ]}
        />
      )}

      <ConfirmPopup
        isVisible={isLoginConfirmPopupVisible}
        text={{
          ...t('pages.doctorDetail.loginConfirmPopup', {
            returnObjects: true,
          }),
        }}
        onConfirm={moveToSignIn}
        onClickBackground={closeLoginConfirmPopup}
        onCancel={closeLoginConfirmPopup}
      />
    </div>
  )
}

const StyledProfileImageWrapper = styled.span`
  position: relative;
  min-width: 100px;
  height: 100px;
`

const StyledDoctorProfileDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const StyledProfile = styled.div`
  display: flex;
  padding: 0 26px;
  gap: 16px;
`

const StyledProfileImage = styled(Image)<{ $isSpecialist?: boolean }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px solid ${({ theme, $isSpecialist }) => ($isSpecialist ? theme.colors.latte : theme.colors.gray300)};
  object-fit: cover;
`

const StyledSpecialistCertificatedIcon = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`

const StyledDoctorNameSpecialtyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledDoctorNameWrapper = styled.span`
  ${({ theme }) => theme.typography.title2}
`

const StyledSpecialistTextWrapper = styled.span`
  ${({ theme }) => theme.typography.caption1}
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledSpecialistText = styled.span`
  color: ${({ theme }) => theme.colors.latte};
`

const StyledDoctorSubtitle = styled.div`
  display: flex;
  gap: 4px;
  ${({ theme }) => theme.typography.description2}
`

const StyledTagsSection = styled.div`
  ${({ theme }) => theme.typography.description2}
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledDivider = styled.div`
  margin: 26px 0;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.gray300};
`

const StyledWarning = styled(Text)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledReviewSectionWrapper = styled.div`
  padding: 0 26px;
`

const StyledSectionTitle = styled.h2`
  margin: unset;
  margin-bottom: 16px;
  ${({ theme }) => theme.typography.title3}
`

const StyledReviewCount = styled.span`
  color: ${({ theme }) => theme.colors.orange};
`

const StyledReviewImagesWrapper = styled.div`
  position: relative;
  display: grid;
  margin-bottom: 16px;
  grid-template-columns: repeat(3, calc((100% - 16px) / 3));
  grid-template-rows: repeat(2, fit-content);
  row-gap: 16px;
  column-gap: 8px;
`

const StyledReviewWrapper = styled.div`
  height: fit-content;
`

const StyledReviewImage = styled.img`
  width: 100%;
  height: 124px;
  border-radius: 8px;
  object-fit: cover;
`

const StyledTreatmentLabel = styled.div`
  ${({ theme }) => theme.typography.description2}
  color: ${({ theme }) => theme.colors.latte};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledReviewButtonLink = styled(Link)`
  text-decoration: none;
`

const StyledButton = styled(Button)`
  width: 100%;
  padding: 8px 0;
`

const StyledReviewButtonInnerText = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledAffiliatedHospitalSection = styled.section`
  padding: 0 26px;
`

const StyledHospitalModule = styled(Link)`
  display: flex;
  gap: 8px;
  text-decoration: none;
  color: inherit;
`

const StyledHospitalName = styled.div`
  ${({ theme }) => theme.typography.title3};
`

const StyledHospitalRatingArea = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledHospitalRatingCount = styled.span`
  color: ${({ theme }) => theme.colors.gray700};
`

const StyledEventInfoArea = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledHospitalEventCount = styled.span`
  color: ${({ theme }) => theme.colors.black};
`

const StyledHospitalDetailInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.typography.caption1};
  & > span {
    &:after {
      content: '|';
      color: ${({ theme }) => theme.colors.gray300};
      margin: 0 8px;
    }
  }
  & > span:last-child {
    &:after {
      content: '';
      margin: 0;
    }
  }
`

const StyledHospitalSubwayName = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledStarIcon = styled(CheetahIcon)`
  margin-right: 4px;
  width: 12px;
  height: 12px;
`

const StyledHospitalInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
`

const StyledHospitalProfileImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 50%;
  overflow: hidden;
`

const StyledCertifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const StyledCertifiedHospitalText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.certified};
`

const StyledExperienceHistoryWrapper = styled.div`
  padding: 0 26px 52px;
`

const StyledExperienceHistory = styled.div`
  ${({ theme }) => theme.typography.description2}
  white-space: pre-line;
`
